import React, { useEffect, useMemo, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import fi from "date-fns/locale/fi";
import sv from "date-fns/locale/sv";
import enGB from "date-fns/locale/en-GB";
import "../../../cssSource/less/calendar.less";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Base } from "../../framework/common/base";
import { AppUtils } from "../../models/common/appUtils";
import { useSelectDailyAllowancesByDate } from "../../hooks/worktime/useSelectWorkHours";
import { useAsyncThunkAction } from "../../hooks/framework/useAsyncThunkAction";
import { fetchCalendarDays } from "../../framework/redux/slices/calendarSlice";
import { useSelectCalendarDay } from "../../hooks/calendar/useCalendar";
import { fetchDayBookings } from "../../framework/redux/slices/dayBookingSlice";
import { handleApiError } from "../../services/baseService";
import { useSelectDaybookingsByDateRange } from "../../hooks/worktime/useSelectDayBookings";
import { DayBooking } from "../../models/dayBooking/dayBooking";
import { useShowWorkShiftEndTimesInMobile } from "../../hooks/framework/useShowWorkShiftEndTimesInMobile";
import CircleIcon from "@mui/icons-material/Circle";
import { commonBoxStyle } from "../layout/mainLayout";
import { useTranslation } from "react-i18next";
import { SvgIconOwnProps } from "@mui/material";
import { useAppOptionValueEquals } from "../../hooks/framework/useAppOptionValue";
import {
    OptionWorkShiftPlanningEnabled,
    OptionRoutesEnabled,
} from "../../models/common/consts";
import {
    cultureCodeEnglish,
    cultureCodeFinnish,
    cultureCodeSwedish,
} from "../../i18n/i18n";
import { fetchWorkShifts } from "../../framework/redux/slices/workShiftSlice";
import {
    ShiftEntryType,
    useSelectCalendarWeekRows,
    WeekRowsByDate,
} from "../../hooks/worktime/useSelectCalendarWeekRows";

interface Props {
    date: string;
    setDate: (date: string) => void;
}

const getDateFnsLocale = (culture: string) => {
    switch (culture) {
        case cultureCodeFinnish:
            return fi;
        case cultureCodeSwedish:
            return sv;
        case cultureCodeEnglish:
        default:
            return enGB;
    }
};

export const WorklogCalendar = ({ date, setDate }: Props) => {
    const [selected, setSelected] = useState(new Date(date));
    const { t, i18n } = useTranslation();

    const datePickerLocale = getDateFnsLocale(i18n.language);

    const [plannedShiftsEnabled] = useAppOptionValueEquals(
        OptionWorkShiftPlanningEnabled,
        true
    );

    const [routesEnabled] = useAppOptionValueEquals(
        OptionRoutesEnabled,
        true
    );

    const showWorkShiftEndTimes = useShowWorkShiftEndTimesInMobile();

    const startDate = useMemo(
        () =>
            Base.dateToDateJSON(
                Base.dateStartOf(Base.dateStartOf(date, "month"), "week")
            ),
        [date]
    );
    const endDate = useMemo(
        () =>
            Base.dateToDateJSON(
                Base.dateEndOf(Base.dateEndOf(date, "month"), "week")
            ),
        [date]
    );

    const weekRowsByDate = useSelectCalendarWeekRows(startDate, endDate);

    useEffect(() => {
        setSelected(new Date(date));
    }, [date]);

    useAsyncThunkAction(
        () =>
            fetchWorkShifts({
                startDate,
                endDate,
            }),
        {
            onError: handleApiError,
        },
        [startDate, endDate]
    );

    useAsyncThunkAction(
        () =>
            fetchCalendarDays({
                startDate: startDate,
                endDate: endDate
            }),
        { onError: () => null },
        [startDate, endDate]
    );

    const dayBookings = useSelectDaybookingsByDateRange(startDate, endDate);

    useAsyncThunkAction(
        () =>
            fetchDayBookings({
                startDate: startDate,
                endDate: endDate,
            }),
        {
            onError: handleApiError,
        },
        [startDate, endDate]
    );

    const handleMonthChange = (date: Date) => {
        setDate(Base.dateToDateJSON(date));
    };

    const renderDayContents = (day: number, date: Date) => {
        return (
            <DayContents
                day={day}
                date={date}
                dayBookingItems={dayBookings}
                routesEnabled={routesEnabled}
                weekRowsByDate={weekRowsByDate}
            />
        );
    };

    const getPlannedHoursLabel = () => {
        if (showWorkShiftEndTimes) {
            return t("workTime.calendar.plannedHours");
        } else {
            return routesEnabled
                ? t("workTime.calendar.route")
                : t("workTime.calendar.shiftStart");
        }
    };

    return (
        <Stack>
            <Box className="worktime-datepicker-container">
                <DatePicker
                    calendarClassName="worktime-datepicker"
                    dateFormat="dd.MM.yyyy"
                    locale={datePickerLocale as any}
                    selected={selected}
                    inline
                    renderDayContents={renderDayContents}
                    onMonthChange={handleMonthChange}
                    onChange={(value: Date) => {
                        setDate(Base.dateToDateJSON(value));
                    }}
                />
            </Box>
            <Box sx={commonBoxStyle}>
                <Stack
                    direction="row"
                    spacing={{ xs: 1, sm: 2, md: 3 }}
                    flexWrap="wrap"
                    justifyContent="center"
                    alignItems="center"
                >
                    {plannedShiftsEnabled && (
                        <CalendarLabelItem
                            color="blue"
                            label={getPlannedHoursLabel()}
                        />
                    )}
                    <CalendarLabelItem
                        color="green"
                        label={showWorkShiftEndTimes ? t("workTime.calendar.actual") : t("workTime.calendar.actualHours")}
                    />
                    <CalendarLabelItem
                        color="orange"
                        label={t("workTime.calendar.dayBookings")}
                    />
                </Stack>
            </Box>
        </Stack>
    );
};

interface ShiftChip {
    rowIdx: number;
    startsBefore: boolean;
    endsAfter: boolean;
    label: string;
    startOffset: boolean;
    length: number;
    type: ShiftEntryType;
}

const DayContents = ({
    day,
    date,
    dayBookingItems,
    routesEnabled,
    weekRowsByDate,
}: {
    day: number;
    date: Date;
    dayBookingItems?: Array<DayBooking>;
    routesEnabled: boolean;
    weekRowsByDate: WeekRowsByDate;
}) => {
    const dateString = Base.dateToDateJSON(date);
    const calendarDay = useSelectCalendarDay(dateString);
    const showWorkShiftEndTimes = useShowWorkShiftEndTimesInMobile();
    const { t } = useTranslation();

    const calcAllowances = useSelectDailyAllowancesByDate(
        dateString,
        dateString,
        true
    );

    const hasDayBookings =
        dayBookingItems?.some(
            (item) => item.day <= dateString && item.endDay >= dateString
        ) ||
        calcAllowances?.some(
            (item) => item.dateStr <= dateString && item.dateStr >= dateString
        );

    const weekStart = Base.dateToDateJSON(Base.dateStartOf(date, "week"));
    const weekRows = weekRowsByDate[weekStart];

    const getPlannedContent = () => {
        const content: ShiftChip[] = [];
        const startSlot = Base.dateDiffInDays(weekStart, dateString) * 2;
        const endSlot = startSlot + 1;

        weekRows?.forEach((row, rowIdx) => {
            row.forEach((r, slotIdx) => {
                if (
                    (r.startSlot === startSlot || r.startSlot === endSlot) &&
                    r.startSlot === slotIdx
                ) {
                    let label = "";
                    const showDuration =
                        showWorkShiftEndTimes || r.type !== "planned";

                    if (showDuration) {
                        label = AppUtils.getDurationStrByDurationMinShort(
                            r.duration
                        );
                    } else if (routesEnabled) {
                        label = r.routeName || t("workTime.calendar.noRoute");
                    } else {
                        label = Base.dateToTimeStr(r.startTime);
                    }

                    content.push({
                        rowIdx,
                        startsBefore: r.startsBefore,
                        endsAfter: r.endsAfter,
                        label,
                        startOffset: r.startSlot === endSlot,
                        length: r.endSlot - r.startSlot,
                        type: r.type,
                    });
                }
            });
        });

        return content;
    };

    const plannedContent = getPlannedContent();

    return (
        <>
            <span
                style={{
                    color: calendarDay?.isHoliday ? "red" : "inherit",
                    lineHeight: "1.5rem",
                    height:
                        weekRows?.length > 1
                            ? `${weekRows?.length * 18 + 30}px`
                            : "50px",
                }}
                className={`day-container rows-${weekRows?.length ?? 0}`}
            >
                <div className={hasDayBookings ? "has-daybookings" : null}>
                    {day}
                </div>
                {plannedContent.map((c, i) => (
                    <p
                        key={i}
                        data-testid={`worklog-calendar-chip-${c.type}-${dateString}-${i}`}
                        className={`worktime-datepicker-hours ${c.type} ${
                            c.startOffset ? " offset" : ""
                        } ${c.startsBefore ? " left-0" : ""} length-${
                            c.length
                        } ${c.endsAfter ? " right-0" : ""} length-${
                            c.length
                        } row-${c.rowIdx + 1}`}
                        style={{
                            width:
                                c.length === 1
                                    ? "66%"
                                    // width is the number of full days times 100
                                    // Plus 66 (33 per each half day)
                                    // Plus pixels for borders
                                    // (Each border is 2px so length equals the borders that have been passed)
                                    : `calc(${
                                          ((c.length - 2) / 2) * 100 + 66
                                      }% + ${c.length}px)`,
                            top: `${18 * c.rowIdx + 30}px`,
                        }}
                    >
                        {c.label}
                    </p>
                ))}
            </span>
        </>
    );
};

interface CalendarLabelItemProps {
    color: SvgIconOwnProps["color"];
    label: string;
}

const CalendarLabelItem = ({ color, label }: CalendarLabelItemProps) => {
    return (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, flex: "0 1 auto" }}>
            <CircleIcon color={color} fontSize="small" />
            <Typography
                variant="body2"
                sx={{
                    color: `${color}.main`,
                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                    fontWeight: 400,
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                    maxWidth: { xs: "80px", sm: "120px", md: "none" }
                }}
            >
                {label}
            </Typography>
        </Stack>
    );
};
